import { useEffect, useRef, useCallback } from 'react';
import { useAuthStore } from '@/application/stores/auth.store';
import { useNotificationStore } from '@/application/stores/notification.store';
import { useQueryClient } from '@tanstack/react-query';

interface WsNotificationPayload {
  type: 'notification';
  data: {
    id: string;
    notificationType: string;
    title: string;
    message: string;
    entityType: string | null;
    entityId: string | null;
    actionUrl: string | null;
    createdAt: string;
  };
}

interface WsUnreadCountPayload {
  type: 'unread_count';
  data: { count: number };
}

type WsPayload = WsNotificationPayload | WsUnreadCountPayload;

const WS_RECONNECT_DELAY = 3000;
const WS_MAX_RECONNECT_ATTEMPTS = 10;

function getWsUrl(token: string): string {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const host = window.location.host;
  return `${protocol}//${host}/ws?token=${encodeURIComponent(token)}`;
}

/**
 * Hook that manages a WebSocket connection for real-time push notifications.
 * Automatically connects when authenticated, reconnects on disconnect,
 * and updates the notification store with incoming data.
 */
export function useWebSocket(): void {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttempts = useRef(0);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const accessToken = useAuthStore((s) => s.accessToken);
  const addToast = useNotificationStore((s) => s.addToast);
  const setUnreadCount = useNotificationStore((s) => s.setUnreadCount);
  const queryClient = useQueryClient();

  const cleanup = useCallback(() => {
    if (reconnectTimer.current !== null) {
      clearTimeout(reconnectTimer.current);
      reconnectTimer.current = null;
    }
    if (wsRef.current !== null) {
      wsRef.current.close();
      wsRef.current = null;
    }
  }, []);

  const connect = useCallback(() => {
    if (accessToken == null || accessToken === '') return;

    const url = getWsUrl(accessToken);
    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      reconnectAttempts.current = 0;
    };

    ws.onmessage = (event) => {
      try {
        const payload = JSON.parse(event.data as string) as WsPayload;

        if (payload.type === 'notification') {
          // Show toast notification
          addToast({
            type: 'info',
            title: payload.data.title,
            message: payload.data.message,
          });

          // Invalidate notifications query to refresh list
          void queryClient.invalidateQueries({ queryKey: ['notifications'] });
        }

        if (payload.type === 'unread_count') {
          setUnreadCount(payload.data.count);
        }
      } catch {
        // Ignore invalid messages
      }
    };

    ws.onclose = (event) => {
      wsRef.current = null;

      // Don't reconnect if closed intentionally or auth issue
      if (event.code === 4001 || event.code === 4002 || event.code === 4003) {
        return;
      }

      // Attempt reconnect
      if (reconnectAttempts.current < WS_MAX_RECONNECT_ATTEMPTS) {
        reconnectAttempts.current += 1;
        const delay = WS_RECONNECT_DELAY * reconnectAttempts.current;
        reconnectTimer.current = setTimeout(() => {
          connect();
        }, delay);
      }
    };

    ws.onerror = () => {
      // Error handled by onclose
    };
  }, [accessToken, addToast, setUnreadCount, queryClient]);

  useEffect(() => {
    if (isAuthenticated && accessToken != null) {
      connect();
    }

    return () => {
      cleanup();
    };
  }, [isAuthenticated, accessToken, connect, cleanup]);
}
